import { queryOptions } from "@tanstack/react-query";
import { STATES } from "@/data/states";
import { JACKPOT_SNAPSHOTS } from "@/data/jackpots";
import type { Game } from "@/data/games";
import { fetchLiveLottery, buildLiveMaps, type LiveByGameMaps } from "@/lib/lottery-api.functions";
import { getGameOverridesCached } from "@/lib/game-overrides-read.functions";

type GameOverrideRow = {
  slug: string;
  name: string | null;
  logo_url: string | null;
  draw_schedule: string | null;
  description: string | null;
  updated_at?: string | null;
};

function applyOverride(g: Game, o?: GameOverrideRow | null): Game {
  if (!o) return g;
  return {
    ...g,
    name: o.name ?? g.name,
    logo_url: o.logo_url ?? g.logo_url,
    draw_schedule: o.draw_schedule ?? g.draw_schedule,
    description: o.description ?? g.description,
  };
}

function attachStates(g: Game) {
  if (g.is_multistate) return { ...g, states: null };
  const st = STATES.find((s) => s.id === g.state_id);
  return {
    ...g,
    states: st
      ? { name: st.name, slug: st.slug, abbreviation: st.abbreviation, official_lottery_url: st.official_lottery_url }
      : null,
  };
}

function mergeJackpot(snap: (typeof JACKPOT_SNAPSHOTS)[number], maps: LiveByGameMaps | undefined) {
  const live = maps?.jackpotByGameId[snap.game_id];
  if (!live) return snap;
  return {
    ...snap,
    jackpot_amount: live.jackpot_amount,
    cash_value: live.cash_value ?? snap.cash_value,
    next_draw_datetime: live.next_draw_iso,
  };
}

export type JackpotDTO = {
  id: string;
  game_id: string;
  jackpot_amount: number;
  cash_value: number | null;
  next_draw_datetime: string | null;
  games: ReturnType<typeof attachStates>;
};

async function fetchOverrides(): Promise<GameOverrideRow[]> {
  try {
    return (await getGameOverridesCached()) as GameOverrideRow[];
  } catch {
    return [];
  }
}

/**
 * Server-renderable jackpot list. Mirrors `useJackpots` but resolves in a single
 * awaited pass so the homepage can render real cards in the SSR HTML instead of
 * a skeleton that flashes and swaps after hydration.
 */
async function buildJackpots(): Promise<JackpotDTO[]> {
  const [live, overrides] = await Promise.all([
    fetchLiveLottery().catch(() => null),
    fetchOverrides(),
  ]);
  const maps = live ? buildLiveMaps(live) : undefined;
  const overrideMap = new Map<string, GameOverrideRow>(overrides.map((o) => [o.slug, o]));

  const base = JACKPOT_SNAPSHOTS.map((j) => {
    const merged = mergeJackpot(j, maps);
    const game = j.games as Game;
    return {
      id: merged.id,
      game_id: merged.game_id,
      jackpot_amount: merged.jackpot_amount,
      cash_value: merged.cash_value ?? null,
      next_draw_datetime: merged.next_draw_datetime ?? null,
      games: attachStates(applyOverride(game, overrideMap.get(game.slug))),
    } satisfies JackpotDTO;
  });

  const seen = new Set(base.map((b) => b.game_id));
  const extras: JackpotDTO[] = (maps?.syntheticGames ?? [])
    .filter((s) => s.jackpot && !seen.has(s.game.id))
    .map((s) => ({
      id: `jp-${s.game.id}`,
      game_id: s.game.id,
      jackpot_amount: s.jackpot!.jackpot_amount,
      cash_value: s.jackpot!.cash_value ?? null,
      next_draw_datetime: s.jackpot!.next_draw_iso ?? null,
      games: attachStates(applyOverride(s.game, overrideMap.get(s.game.slug))),
    }));

  return [...base, ...extras];
}

/**
 * Cached wrapper. Jackpot cards are identical for every visitor, so one
 * computation per minute per worker serves any amount of traffic; a stale
 * value is returned instantly while the refresh happens in the background.
 */
export async function computeJackpots(): Promise<JackpotDTO[]> {
  const { cached } = await import("@/lib/server-cache");
  return cached("ssr:jackpots", 60_000, buildJackpots, {
    staleWhileRevalidateMs: 10 * 60_000,
  });
}

export const jackpotsQueryOptions = () =>
  queryOptions({
    queryKey: ["jackpots-ssr"] as const,
    queryFn: computeJackpots,
    staleTime: 5 * 60 * 1000,
  });
